<template>
  <div class="chart-wrapper">
    <ChartDownloadButton
      :title="chartTitle"
      :get-chart="getChartInstance"
      :disabled="loading"
    />
    <div class="chart-title">{{ chartTitle }}</div>
    <ChartSpinner :visible="loading" :show-watermark="false" />
    <v-chart
      ref="vueChartRef"
      :option="chartOption"
      autoresize
      style="width: 100%; height: 420px;"
    />
  </div>
</template>

<script>
import { defineComponent, ref, watch, nextTick } from 'vue';
import { useResponsiveCharts } from '@/utils/useResponsiveCharts.js'; 
import VChart from 'vue-echarts';
import ChartSpinner from '@/components/common/ChartSpinner.vue';
import ChartDownloadButton from '@/components/dashboard/charts/ChartDownloadButton.vue';
import chartColors from '@/utils/chartColors.js';
import { appendWatermark } from '@/utils/chartWatermark';

export default defineComponent({
  name: 'SinglePieChart',
  components: {
    'v-chart': VChart,
    ChartSpinner,
    ChartDownloadButton
  },
  props: {
    chartTitle: {
      type: String,
      default: '饼图'
    },
    chartData: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const { isMobile } = useResponsiveCharts();
    const chartOption = ref({});
    const loading = ref(false);
    const vueChartRef = ref(null);
    const getChartInstance = () => {
      const vm = vueChartRef.value;
      if (!vm) return null;
      if (vm.chart && typeof vm.chart.getDom === 'function') return vm.chart;
      return vm;
    };

    const buildChart = () => {
      loading.value = true;
      setTimeout(() => {
        const radius = isMobile.value ? '40%' : '45%';
        const labelFontSize = isMobile.value ? 10 : 12;

        chartOption.value = {
          color: chartColors,
          tooltip: { trigger: 'item' },
          legend: { show: false },
          series: [{
            type: 'pie',
            radius: radius, 
            data: props.chartData,
            label: {
              show: true,
              formatter: ({ name, value }) => `${name}: ${value}`,
              position: 'outside',
              fontSize: labelFontSize,
              width: 80,
              overflow: 'breakAll',
              lineHeight: 15
            },
            labelLine: { 
              show: true,
              length: isMobile.value ? 15 : 15,
              length2: isMobile.value ? 20 : 20
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }]
        };
        appendWatermark(chartOption.value);
        loading.value = false;

        nextTick(() => {
          vueChartRef.value?.chart?.resize();
        });
      }, 500);
    };

    watch(() => props.chartData, buildChart, { immediate: true, deep: true });
    watch(isMobile, buildChart);

    return {
      chartOption,
      loading,
      vueChartRef,
      getChartInstance
    };
  }
});
</script>

<style scoped>
.chart-wrapper {
  display: flex;
  flex-direction: column;
  width: 90%;
  align-items: center;
  margin-top: 20px;
  position: relative;
}
.chart-title {
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  color: #2e3968;
  margin-bottom: 10px;
}

@media (max-width: 768px) {
  .chart-wrapper {
    width: 150%;
    box-sizing: border-box;
  }
  .chart-title {
    position: relative;
    transform: translateX(-50%);
    top: 10px;
    margin: 0;
    font-size: 16px;
  }
}
</style>